import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, api, Badge, Button, Card, Modal, Spinner, useFetch } from '@greenrinse/ui';
import type { Country } from './CountriesPage';
import { Partner, PartnerForm } from './PartnersPage';
import SitesPage from './SitesPage';
import FleetPage from './FleetPage';
import ContractsPage from './ContractsPage';
import UsersPage from './UsersPage';
import { ApiWebhooksPage } from '@greenrinse/features';

const TABS = ['Overview', 'Sites', 'Fleet', 'Contracts', 'Users', 'API & Webhooks'] as const;

export default function PartnerDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const partner = useFetch<Partner>(`/api/partners/${id}`);
  const countries = useFetch<Country[]>('/api/countries');
  const [tab, setTab] = useState<typeof TABS[number]>('Overview');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (!confirm(`Deactivate and delete partner "${partner.data?.name}"?`)) return;
    try { await api(`/api/partners/${id}`, { method: 'DELETE' }); navigate('/partners'); } catch (e) { setError((e as Error).message); }
  };

  if (partner.loading) return <Spinner />;
  if (!partner.data) return <Alert tone="error">{partner.error ?? 'Partner not found'}</Alert>;
  const p = partner.data;

  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <div>
          <button className="gr-btn gr-btn--ghost" style={{ minHeight: 28, padding: 0 }} onClick={() => navigate('/partners')}>← Partners</button>
          <h1 style={{ margin: '4px 0 0' }}>{p.name} {p.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</h1>
          <span className="gr-muted">{p.legalName ?? ''} {p.countryCode}</span>
        </div>
        <div className="gr-row"><Button variant="outline" onClick={() => setEditing(true)}>Edit</Button><Button variant="danger" onClick={() => void remove()}>Delete</Button></div>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <div className="gr-tabs">{TABS.map((t) => <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{t}</button>)}</div>

      {tab === 'Overview' && (
        <div className="gr-grid">
          <Card small><div className="gr-muted" style={{ fontSize: 14 }}>Sites</div><div className="gr-stat">{p.siteCount}</div></Card>
          <Card small><div className="gr-muted" style={{ fontSize: 14 }}>Active vehicles</div><div className="gr-stat">{p.vehicleCount}</div></Card>
          <Card small><div className="gr-muted" style={{ fontSize: 14 }}>Active contracts</div><div className="gr-stat">{p.activeContractCount}</div></Card>
          <Card small style={{ gridColumn: '1 / -1' }}>
            <dl className="gr-kv">
              <dt>Tax ID</dt><dd>{p.taxId ?? '—'}</dd>
              <dt>Billing email</dt><dd>{p.billingEmail ?? '—'}</dd>
              <dt>Billing address</dt><dd>{p.billingAddress ?? '—'}</dd>
              <dt>Contact</dt><dd>{[p.contactName, p.contactPhone].filter(Boolean).join(' · ') || '—'}</dd>
            </dl>
          </Card>
        </div>
      )}
      {tab === 'Sites' && <SitesPage partnerId={id} />}
      {tab === 'Fleet' && <FleetPage partnerId={id} />}
      {tab === 'Contracts' && <ContractsPage partnerId={id} />}
      {tab === 'Users' && <UsersPage partnerId={id} />}
      {tab === 'API & Webhooks' && <ApiWebhooksPage partnerId={id} />}

      <Modal open={editing} title="Edit partner" onClose={() => setEditing(false)}>
        <PartnerForm partner={p} countries={countries.data ?? []} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); partner.reload(); }} />
      </Modal>
    </div>
  );
}
